import { Card, Checkbox, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { ApiUser } from '@api/interfaces';
import { ApiEnergyCustomer } from '@api/interfaces/ApiEnergyCustomer';
import { Stack, Tooltip } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

type Memberships = ApiEnergyCustomer['memberships'];

interface ProgramMembershipCardProps {
  customer: ApiUser,
  energyCustomer: ApiEnergyCustomer,
  isReadOnly: boolean,
}

const withTooltip = (title: string | undefined, child: ReactElement) => (
  title ? <Tooltip title={title}><span>{child}</span></Tooltip> : child
);

export const ProgramMembershipCard = ({
  customer,
  energyCustomer,
  isReadOnly,
}: ProgramMembershipCardProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { memberships } = energyCustomer;

  const { isLoading, mutate: setMemberships } = useMutation(
    (next: Memberships) => EnergyApi.setMemberships(customer.id, next),
    {
      onError: (error: { message?: string }) => {
        openSnackbar(error.message || t(($) => $.energy.programModal.errors.generic));
      },
      onSuccess: async (updated) => {
        queryClient.setQueryData([QueryKeys.GET_ENERGY_CUSTOMER, customer.id], updated);
        await queryClient.invalidateQueries([QueryKeys.GET_USER, customer.id]);
        openSnackbar(t(($) => $.energy.profile.membershipSaved));
      },
    },
  );

  const isLastSelected = (program: keyof Memberships) => (
    memberships[program] && !memberships[program === 'agronomy' ? 'energy' : 'agronomy']
  );
  const cannotJoinEnergyReason = !memberships.energy && !energyCustomer.canJoinEnergy
    ? (customer.accountGroupRole
      ? t(($) => $.energy.profile.linkedAccountGroup)
      : t(($) => $.energy.profile.noEnergyProgram))
    : undefined;

  const renderToggle = (program: keyof Memberships, label: string, blockedReason?: string) => {
    const lastSelectedReason = isLastSelected(program)
      ? t(($) => $.energy.profile.lastProgram)
      : undefined;
    const reason = isReadOnly ? undefined : (blockedReason ?? lastSelectedReason);
    return withTooltip(reason, (
      <Checkbox
        checked={memberships[program]}
        disabled={isReadOnly || isLoading || !!reason}
        onChangeChecked={(checked) => setMemberships({ ...memberships, [program]: checked })}
        testID={`membership-${program}`}
      >
        <Text category="body-medium">{label}</Text>
      </Checkbox>
    ));
  };

  return (
    <Card testID="program-membership-card">
      <Text category="overline">{t(($) => $.energy.profile.membershipTitle)}</Text>
      <VSpacer size="2" />
      <Text category="body-small" sx={{ color: 'text.secondary' }}>
        {t(($) => $.energy.profile.membershipCopy)}
      </Text>
      <VSpacer size="3" />
      <Stack direction="row" spacing={2}>
        {renderToggle('agronomy', t(($) => $.energy.profile.agronomy))}
        {renderToggle('energy', t(($) => $.energy.profile.energy), cannotJoinEnergyReason)}
      </Stack>
    </Card>
  );
};
